"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, PhoneCall, Video, Briefcase, ArrowRight, Clock, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const channels = [{
  title: "Atención presencial",
  badge: "Sede central",
  description: "Reunión privada y ejecutiva en nuestras oficinas corporativas en Los Mochis para el análisis directo de expedientes y acuerdos.",
  detail: "Sesión personal en sala de juntas",
  icon: Building2,
  availability: "Previa cita programada",
  actionLabel: "Agendar visita",
  to: "/ContactPage"
}, {
  title: "Llamada telefónica",
  badge: "Atención directa",
  description: "Enlace telefónico confidencial e inmediato con un especialista sénior asignado para resolver dudas puntuales o criterios normativos.",
  detail: "Línea directa sin intermediarios",
  icon: PhoneCall,
  availability: "Horario hábil continuo",
  actionLabel: "Solicitar llamada",
  to: "/ContactPage"
}, {
  title: "Videollamada",
  badge: "Plataforma remota",
  description: "Sesión interactiva en tiempo real con compartición de pantalla para revisión conjunta de balanzas, auditorías y documentos legales.",
  detail: "Revisión documental en vivo",
  icon: Video,
  availability: "Nacional e internacional",
  actionLabel: "Coordinar sesión",
  to: "/ContactPage"
}, {
  title: "Asesoría de mayor alcance",
  badge: "Diagnóstico integral",
  description: "Acompañamiento continuo bajo póliza mensual o auditoría forense con asignación de equipo multidisciplinario dedicado.",
  detail: "Dictamen y seguimiento periódico",
  icon: Briefcase,
  availability: "Contratación corporativa",
  actionLabel: "Solicitar propuesta",
  to: "/ContactPage"
}];
export default function Modalities() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="modalidades-de-atención" data-nav="light" className="relative bg-background py-20 lg:py-28 border-b border-border selection:bg-accent selection:text-foreground">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/5 border border-ring/40 rounded-xs mb-4">
            <span className="w-1.5 h-1.5 bg-ring rounded-full" />
            <span className="font-serif text-xs font-bold uppercase tracking-widest text-foreground">
              Formatos de consulta
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-wide text-foreground uppercase leading-tight">
            Modalidades de atención
          </h2>

          {/* ASV Gold Hairline Accent */}
          <div className="w-12 h-[1.5px] bg-ring my-5" />

          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            Elija el canal idóneo para revisar su situación contable, fiscal o jurídica. Nuestro equipo multidisciplinario garantiza confidencialidad, rigor técnico y atención personalizada en cada formato.
          </p>
        </div>

        {/* 4-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {channels.map((channel, i) => {
          const Icon = channel.icon;
          return <motion.div key={channel.title} data-index={i} variants={itemVariants} className="group relative flex flex-col justify-between bg-card rounded-xs border border-border pt-0 pb-8 px-6 transition-[border-color,box-shadow] duration-300 hover:border-ring/60 hover:shadow-md">
                {/* 2px Satin Gold Hairline Top Border */}
                <div className="w-full h-[2px] bg-ring mb-7" />

                <div>
                  {/* Top metadata row */}
                  <div className="flex items-center justify-between gap-2 mb-6">
                    <div className="w-10 h-10 rounded-xs border border-ring/40 flex items-center justify-center bg-background text-foreground group-hover:text-primary transition-colors">
                      <Icon className="w-5 h-5 text-ring" />
                    </div>
                    <span className="font-serif text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-0.5 bg-muted/60 rounded-xs">
                      {channel.badge}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-serif text-lg font-semibold tracking-wide text-foreground uppercase mb-3 leading-snug">
                    {channel.title}
                  </h3>

                  {/* Operational Description */}
                  <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-6">
                    {channel.description}
                  </p>

                  {/* Institutional bullet points */}
                  <div className="space-y-2.5 pt-4 border-t border-border/70 mb-8">
                    <div className="flex items-start gap-2 text-xs text-foreground/80 font-sans">
                      <ShieldCheck className="w-4 h-4 text-ring shrink-0 mt-0.5" />
                      <span>{channel.detail}</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-muted-foreground font-sans">
                      <Clock className="w-4 h-4 text-ring/80 shrink-0 mt-0.5" />
                      <span>{channel.availability}</span>
                    </div>
                  </div>
                </div>

                {/* Bottom CTA Button */}
                <div>
                  <Button asChild variant="outline" className="w-full justify-between h-10 border-border text-foreground hover:border-ring hover:bg-accent/40 font-serif text-xs font-semibold uppercase tracking-wider transition-colors rounded-xs group/btn">
                    <Link to={channel.to}>
                      <span>{channel.actionLabel}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-ring transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Supporting Institutional Guarantee Strip */}
        <div className="mt-14 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <p className="font-sans text-xs text-muted-foreground">
            Sede Los Mochis: Av. Rosa Morada 1360, Fracc. Praderas. Atención de lunes a viernes: 9:00 - 18:00 hrs.
          </p>
          <div className="flex items-center gap-6">
            <span className="font-serif text-xs font-bold uppercase tracking-widest text-foreground">
              Estrategia · Cumplimiento · Resultados
            </span>
          </div>
        </div>
      </div>
    </section>;
}